import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Save, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Settings → System → Alerts → "Unanswered ticket reminder"
//
// Controls the red "SLA violation" banner on the Admin Dashboard.
//   • enabled         — full on/off switch. When off, the banner never shows.
//   • threshold_hours — how many hours after the last customer message we
//                       start surfacing the ticket. 4h is the default; admins
//                       can lower (e.g. 2h) for tighter SLAs or raise (24h)
//                       if they only want long-pending ones flagged.
//
// Both values live in `app_settings.ticket_sla_alert` and are managed via
// `GET/PUT /api/orders/tickets/sla-settings`. Per-ticket dismiss ("no reply
// needed") is a separate mechanism handled inline on the dashboard.
export default function TicketSlaAlertEditor({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [hours, setHours] = useState(4);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/tickets/sla-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await r.json();
        if (!alive) return;
        setEnabled(j.enabled !== false);
        setHours(Number(j.threshold_hours) || 4);
      } catch (e) {
        toast.error('Failed to load alert settings');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const save = async () => {
    const h = Math.max(1, Math.min(168, parseInt(hours, 10) || 4));
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/tickets/sla-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled, threshold_hours: h }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setEnabled(j.enabled !== false);
      setHours(Number(j.threshold_hours) || 4);
      toast.success('Alert settings saved');
    } catch (e) {
      toast.error('Save failed: ' + (e?.message || String(e)));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6" data-testid="ticket-sla-alert-editor">
      <div>
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          Unanswered-ticket reminder
        </h2>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">
          Controls the red SLA banner on the Admin Dashboard. When a customer
          writes and no admin replies for longer than the threshold, the banner
          surfaces the ticket. Each ticket can also be dismissed individually
          on the dashboard (&quot;× — no reply needed&quot;); the dismiss is reset the
          next time the customer writes.
        </p>
      </div>

      {/* On/Off switch */}
      <div
        className={`flex items-center justify-between gap-4 p-4 rounded-lg border-2 transition-colors ${
          enabled ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-slate-50'
        }`}
      >
        <div className="flex items-start gap-3 min-w-0">
          {enabled ? (
            <Bell className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : (
            <BellOff className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 text-sm">
              {enabled ? 'Reminder ON' : 'Reminder OFF — banner hidden entirely'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {enabled
                ? 'Banner appears on the dashboard whenever a ticket has waited longer than the threshold.'
                : 'No banner will be shown regardless of how long tickets wait. Toggle this back on to re-enable.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEnabled((v) => !v)}
          data-testid="ticket-sla-alert-toggle"
          className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400 ${
            enabled ? 'bg-emerald-500' : 'bg-slate-300'
          }`}
          aria-pressed={enabled}
          aria-label="Toggle unanswered-ticket reminder"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              enabled ? 'translate-x-6' : ''
            }`}
          />
        </button>
      </div>

      {/* Threshold input — disabled when reminder is off */}
      <div className={`p-4 rounded-lg border ${enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
        <label className="block text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          Threshold — hours of admin silence before alert
        </label>
        <p className="text-xs text-slate-500 mb-3">
          A ticket appears in the banner once the last customer message is older
          than this many hours and no admin has replied since. Range: 1–168 (1 hour to 1 week).
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={1}
            max={168}
            step={1}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            disabled={!enabled}
            data-testid="ticket-sla-alert-threshold"
            className="w-24 h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-cyan-500 outline-none disabled:bg-slate-100 disabled:cursor-not-allowed font-mono"
          />
          <span className="text-sm text-slate-600">hours</span>
          <div className="ml-auto flex gap-1.5">
            {[2, 4, 8, 24].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setHours(preset)}
                disabled={!enabled}
                className="px-2.5 py-1 text-xs rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed font-mono"
              >
                {preset}h
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="ticket-sla-alert-save"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm shadow hover:shadow-md disabled:opacity-50 transition-shadow"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
